import { useMemo, useState } from 'react';

import { AreaChart, DonutChart, RankedBars, StackedColumns } from '../components/charts';
import { CategoryIcon, IconArchive, IconChart, IconDownload } from '../components/icons';
import { LargeTitle } from '../components/layout';
import { categoryName } from '../components/receipts';
import { Button, Card, EmptyState, Segmented, Skeleton } from '../components/ui';
import { useI18n } from '../i18n';
import { exportUrl, type ExpenseFilters } from '../lib/api';
import { useLedgerContext } from '../lib/ledger-context';
import { useSummary } from '../lib/queries';
import { accentVar } from '../lib/ui';

type Range = 'all' | 'thisYear' | 'lastYear';

function rangeToFilters(range: Range): ExpenseFilters {
  const year = new Date().getUTCFullYear();
  if (range === 'thisYear') return { from: `${year}-01-01`, to: `${year}-12-31` };
  if (range === 'lastYear') return { from: `${year - 1}-01-01`, to: `${year - 1}-12-31` };
  return {};
}

export function InsightsScreen() {
  const { t, money, date, locale } = useI18n();
  const [range, setRange] = useState<Range>('all');

  // Scoped to whatever the switcher is showing, like every other screen.
  // Without the ledger ids this totalled every ledger the viewer can read, so
  // Insights disagreed with the dashboard sitting next to it.
  const { filterIds } = useLedgerContext();
  const filters = useMemo(
    () => ({ ...rangeToFilters(range), ledgerId: filterIds }),
    [range, filterIds],
  );
  const query = useSummary(filters);
  const summary = query.data;

  const categoryItems = useMemo(
    () =>
      (summary?.byCategory ?? [])
        .filter((row) => row.spentCents > 0)
        .map((row) => ({
          id: row.categoryId ?? 'none',
          label: row.nameEn
            ? categoryName({ nameEn: row.nameEn, nameZh: row.nameZh ?? row.nameEn }, locale)
            : t('common.uncategorized'),
          color: accentVar(row.color),
          value: row.spentCents,
          secondary: row.availableCents,
          icon: <CategoryIcon glyph={row.icon} size={16} />,
        })),
    [summary, locale, t],
  );

  const personItems = useMemo(
    () =>
      (summary?.byPerson ?? [])
        .filter((row) => row.spentCents > 0)
        .map((row) => ({
          id: row.personId ?? 'none',
          label: row.name ?? t('common.unknown'),
          color: accentVar(row.color),
          value: row.spentCents,
        })),
    [summary, t],
  );

  const monthly = useMemo(
    () =>
      (summary?.byMonth ?? []).map((month) => ({
        key: month.month,
        label: date(`${month.month}-01`, 'monthYear'),
        claimed: month.reimbursedCents,
        open: Math.max(month.spentCents - month.reimbursedCents, 0),
      })),
    [summary, date],
  );

  const cumulative = useMemo(() => {
    let running = 0;
    return (summary?.byMonth ?? []).map((month) => {
      running += month.availableCents;
      return { key: month.month, label: date(`${month.month}-01`, 'monthYear'), value: running };
    });
  }, [summary, date]);

  if (query.isLoading) {
    return (
      <>
        <LargeTitle title={t('insights.title')} />
        <div className="space-y-4 px-4 lg:px-8">
          <Skeleton className="h-52 w-full" />
          <Skeleton className="h-52 w-full" />
        </div>
      </>
    );
  }

  const hasData = (summary?.headline.count ?? 0) > 0;

  return (
    <>
      <LargeTitle title={t('insights.title')} subtitle={t('insights.subtitle')}>
        <div className="mt-4">
          <Segmented
            value={range}
            onChange={setRange}
            options={[
              { value: 'all', label: t('insights.allTime') },
              { value: 'thisYear', label: t('receipts.filter.thisYear') },
              { value: 'lastYear', label: t('receipts.filter.lastYear') },
            ]}
          />
        </div>
      </LargeTitle>

      <div className="space-y-6 px-4 pt-4 lg:px-8">
        {!hasData ? (
          <Card>
            <EmptyState
              icon={<IconChart size={38} />}
              title={t('insights.noData')}
              body={t('insights.noDataHelp')}
            />
          </Card>
        ) : (
          <>
            <Card>
              <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
                <div>
                  <div className="text-[12px] text-[var(--ink-secondary)]">
                    {t('dashboard.totalSpent')}
                  </div>
                  <div className="numeric mt-1 text-[22px] font-semibold text-[var(--ink)]">
                    {money(summary!.headline.spentCents, { round: true })}
                  </div>
                </div>
                <div>
                  <div className="text-[12px] text-[var(--ink-secondary)]">
                    {t('insights.claimed')}
                  </div>
                  <div className="numeric mt-1 text-[22px] font-semibold text-[var(--ink)]">
                    {money(summary!.headline.reimbursedCents, { round: true })}
                  </div>
                </div>
                <div>
                  <div className="text-[12px] text-[var(--ink-secondary)]">
                    {t('dashboard.availableToReimburse')}
                  </div>
                  <div className="numeric mt-1 text-[22px] font-semibold text-[var(--accent)]">
                    {money(summary!.headline.availableCents, { round: true })}
                  </div>
                </div>
              </div>
            </Card>

            {monthly.length > 1 ? (
              <Card>
                <StackedColumns
                  title={t('insights.monthlyTitle')}
                  description={t('insights.claimedVsOpen')}
                  points={monthly}
                  formatValue={(value) => money(value, { round: true })}
                />
              </Card>
            ) : null}

            {cumulative.length > 1 ? (
              <Card>
                <AreaChart
                  title={t('dashboard.cumulativeClaimable')}
                  points={cumulative}
                  formatValue={(value) => money(value, { round: true })}
                />
              </Card>
            ) : null}

            {categoryItems.length > 0 ? (
              <Card>
                <RankedBars
                  title={t('insights.byCategoryTitle')}
                  items={categoryItems}
                  formatValue={(value) => money(value, { round: true })}
                  secondaryLabel={t('insights.stillOpen')}
                  limit={20}
                />
              </Card>
            ) : null}

            {personItems.length > 1 ? (
              <Card>
                <DonutChart
                  title={t('insights.byPersonTitle')}
                  items={personItems}
                  formatValue={(value) => money(value, { round: true })}
                />
              </Card>
            ) : null}

            <Card>
              <h3 className="text-[15px] font-semibold text-[var(--ink)]">
                {t('insights.exportArchive')}
              </h3>
              <p className="mt-1 text-[13px] leading-snug text-[var(--ink-secondary)]">
                {t('insights.exportArchiveHelp')}
              </p>
              <div className="mt-4 flex flex-wrap gap-2.5">
                <a href={exportUrl('csv', filters)} download>
                  <Button variant="secondary" icon={<IconDownload size={17} />}>
                    {t('insights.exportCsv')}
                  </Button>
                </a>
                <a href={exportUrl('archive', filters)} download>
                  <Button variant="secondary" icon={<IconArchive size={17} />}>
                    {t('insights.exportArchive')}
                  </Button>
                </a>
              </div>
            </Card>
          </>
        )}
      </div>
    </>
  );
}
